import { Alert, AlertColor, Grid, Snackbar, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { InputField } from '../../../components/InputController';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { createSenderIdentity, resetCreateSenderIdentity, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { useParams } from 'react-router';
import { CreateSenderIdentityDto } from '@inosio/payments-sdk';

const validationSchema = Yup.object({
  Email: Yup.string().trim().email('Invalid email.').required('Email is required.'),
  Name: Yup.string().trim().required('Name is required.'),
  ReplyTo: Yup.string().trim().nullable().email('Invalid reply-to email.').notRequired(),
  ReplyToName: Yup.string().trim().nullable().notRequired(),
});

type schemaValidationType = Yup.InferType<typeof validationSchema>;

export const SenderIdentityForm = () => {
  const { uuid: propertyGroupUuid } = useParams<{ uuid: string }>();
  const classes = useStyle();
  const dispatch = useDispatch();
  const [snackBar, setSnackBar] = useState<{ severity: AlertColor; message?: string } | null>(null);
  const { isCreatingSenderIdentity, isCreatingSenderIdentityFailed, isCreatingSenderIdentitySuccess, CreateSenderIdentityError } = useSelector(selectPropertyGroupDetailsState);

  const formik = useFormik<schemaValidationType>({
    initialValues: {
      Email: '',
      Name: '',
      ReplyTo: '',
      ReplyToName: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const createSenderIdentityDto: CreateSenderIdentityDto = {
        PropertyGroupUuid: propertyGroupUuid,
        Email: values.Email,
        Name: values.Name,
        ReplyTo: values.ReplyTo || undefined,
        ReplyToName: values.ReplyToName || undefined,
      };
      dispatch(
        createSenderIdentity({
          propertyGroupUuid: propertyGroupUuid,
          createSenderIdentityDto: createSenderIdentityDto,
        }),
      );
    },
  });

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBar(null);
  };

  useEffect(() => {
    if (isCreatingSenderIdentityFailed && CreateSenderIdentityError.statusCode) {
      setSnackBar({
        severity: 'error',
        message: CreateSenderIdentityError.message,
      });
      dispatch(resetCreateSenderIdentity());
    }
  }, [dispatch, isCreatingSenderIdentityFailed, CreateSenderIdentityError]);

  useEffect(() => {
    if (isCreatingSenderIdentitySuccess) {
      setSnackBar({
        severity: 'success',
        message: 'Sender Identity Added',
      });
      formik.resetForm();
      dispatch(resetCreateSenderIdentity());
    }
  }, [dispatch, formik, isCreatingSenderIdentitySuccess]);

  return (
    <Grid container component="form" onSubmit={formik.handleSubmit} item sx={{ display: 'flex', flexDirection: 'row', paddingTop: '10px' }}>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={!!snackBar} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={snackBar?.severity}>
          {snackBar?.message}
        </Alert>
      </Snackbar>
      <Grid container spacing={2}>
        {/* Email Address Field */}
        <Grid item xs={12} md={5} lg={5} container spacing={2}>
          <Grid item xs={12} lg={3}>
            <Typography sx={{ fontSize: '15px', marginTop: '5px' }}>Email Address:</Typography>
          </Grid>
          <Grid item xs={12} lg={9}>
            <InputField
              isSlim
              isfullWidth={false}
              label={undefined}
              {...formik.getFieldProps('Email')}
              errors={formik.errors.Email}
              isError={!!formik.errors?.Email && formik.touched.Email}
              touched={formik.touched?.Email}
            />
          </Grid>
        </Grid>

        {/* Name Field */}
        <Grid item xs={12} md={5} lg={5} container spacing={2}>
          <Grid item xs={12} lg={3}>
            <Typography sx={{ fontSize: '15px', marginTop: '5px' }}>Display Name:</Typography>
          </Grid>
          <Grid item xs={12} lg={9}>
            <InputField
              isSlim
              isfullWidth={false}
              label={undefined}
              {...formik.getFieldProps('Name')}
              errors={formik.errors.Name}
              isError={!!formik.errors?.Name && formik.touched.Name}
              touched={formik.touched?.Name}
            />
          </Grid>
        </Grid>

        {/* Reply To Email Field */}
        {/* <Grid item xs={12} md={6} lg={6} container spacing={2}>
          <Grid item xs={12} lg={3}>
            <Typography sx={{ fontSize: '15px', marginTop: '5px' }}>Reply To Email:</Typography>
          </Grid>
          <Grid item xs={12} lg={9}>
            <InputField
              isSlim
              isfullWidth={false}
              label={undefined}
              {...formik.getFieldProps('ReplyTo')}
              errors={formik.errors.ReplyTo}
              isError={!!formik.errors?.ReplyTo && formik.touched.ReplyTo}
              touched={formik.touched?.ReplyTo}
            />
          </Grid>
        </Grid> */}

        {/* Reply To Name Field */}
        {/* <Grid item xs={12} md={6} lg={6} container spacing={2}>
          <Grid item xs={12} lg={3}>
            <Typography sx={{ fontSize: '15px', marginTop: '5px' }}>Reply To Name:</Typography>
          </Grid>
          <Grid item xs={12} lg={9}>
            <InputField
              isSlim
              isfullWidth={false}
              label={undefined}
              {...formik.getFieldProps('ReplyToName')}
              errors={formik.errors.ReplyToName}
              isError={!!formik.errors?.ReplyToName && formik.touched.ReplyToName}
              touched={formik.touched?.ReplyToName}
            />
          </Grid>
        </Grid> */}
        <Grid item xs={12} md={2} lg={2}  display="flex" justifyContent="flex-end">
          <LoadingButton loading={isCreatingSenderIdentity} type="submit" variant="accountManagement" className={classes.AddIdentityButtonStyle}>
            Add Identity
          </LoadingButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const useStyle = makeStyles((theme) => ({
  AddIdentityButtonStyle: {
    backgroundColor: '#fff !important',
    color: '#39A2DB !important',
    border: '2px solid #39A2DB !important',
    boxShadow: 'none !important',
    borderRadius: '24px !important',
    fontFamily: "'Open Sans', sans-serif !important",
    fontSize: '14px !important',
    fontWeight: '700 !important',
    fontStyle: 'normal !important',
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: '#39A2DB !important',
      color: 'white !important',
      border: '2px solid #39A2DB !important',
    },
    width: '150px !important',
    height: '33px !important',
    marginLeft: '20px !important',
  },
}));
